import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Loader2, Check, AlertTriangle, SkipForward } from 'lucide-react';
import {
  listBatchDrafts,
  confirmBatchDrafts,
  updateBatchDraft,
  preprocessInvoice,
  type BatchDraft,
  type BatchConfirmItem,
} from '@/services/invoiceService';
import { fetchWithAuth } from '@/services/apiClient';
import { getFileContentUrl } from '@/services/docflowService';

interface BatchInvoiceReviewProps {
  onComplete: () => void;
  onCancel: () => void;
}

// Cache for preprocessed page images per source_file_id
interface PageImageCache {
  pageImage: string;
  pageMime: string;
  pageWidth: number;
  pageHeight: number;
}

interface BBox { x: number; y: number; w: number; h: number }

const BBOX_COLORS: Record<string, string> = {
  invoice_number: '#3b82f6',
  invoice_date: '#8b5cf6',
  seller_name: '#059669',
  seller_unified_number: '#10b981',
  buyer_name: '#d97706',
  buyer_unified_number: '#f59e0b',
  sale_amount: '#0ea5e9',
  tax_amount: '#ec4899',
  total_amount: '#ef4444',
  order_id: '#64748b',
};

const FIELD_ORDER = [
  { key: 'invoice_id', label: 'Invoice ID', metaKey: 'invoice_number' },
  { key: 'invoice_date', label: 'Invoice Date', metaKey: 'invoice_date' },
  { key: 'seller_name', label: 'Seller Name', metaKey: 'seller_name' },
  { key: 'seller_id', label: 'Seller ID', metaKey: 'seller_unified_number' },
  { key: 'buyer_name', label: 'Buyer Name', metaKey: 'buyer_name' },
  { key: 'buyer_id', label: 'Buyer ID', metaKey: 'buyer_unified_number' },
  { key: 'sale_amount', label: 'Sale Amount', metaKey: 'sale_amount' },
  { key: 'tax_amount', label: 'Tax Amount', metaKey: 'tax_amount' },
  { key: 'total_amount', label: 'Total Amount', metaKey: 'total_amount' },
  { key: 'order_id', label: 'Order ID', metaKey: 'order_id' },
];

const BatchInvoiceReview: React.FC<BatchInvoiceReviewProps> = ({ onComplete, onCancel }) => {
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState<BatchDraft[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [confirmedRows, setConfirmedRows] = useState<Set<number>>(new Set());
  const [skippedRows, setSkippedRows] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({ total: 0, draft_count: 0, confirmed_count: 0, error_count: 0 });

  // Page image cache: source_file_id → PageImageCache
  const [imageCache, setImageCache] = useState<Record<string, PageImageCache>>({});
  const [imageLoading, setImageLoading] = useState<string | null>(null);

  // Edited field values: row_index → { field_key → value }
  const [editedFields, setEditedFields] = useState<Record<number, Record<string, string>>>({});
  const [savingDraft, setSavingDraft] = useState(false);
  const prevTabRef = useRef(activeTab);

  useEffect(() => { loadDrafts(); }, []);

  const loadDrafts = async () => {
    setLoading(true);
    try {
      const result = await listBatchDrafts('draft');
      setDrafts(result.drafts || []);
      setStats({
        total: result.total,
        draft_count: result.draft_count,
        confirmed_count: result.confirmed_count,
        error_count: result.error_count,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load drafts');
    } finally {
      setLoading(false);
    }
  };

  // Load page image for a draft (on-demand, cached)
  const loadPageImage = useCallback(async (draft: BatchDraft) => {
    if (!draft.source_file_id || imageCache[draft.source_file_id]) return;

    setImageLoading(draft.source_file_id);
    try {
      // Download file from Drive
      const url = getFileContentUrl(draft.source_file_id);
      const response = await fetchWithAuth(url, {
        method: 'GET',
        headers: { 'Content-Type': '' },
      });
      const mimeType = response.headers.get('Content-Type') || 'application/pdf';
      const blob = await response.blob();
      const base64 = await blobToBase64(blob);

      // Call /preprocess to render PNG
      const result = await preprocessInvoice({
        file_base64: base64,
        mime_type: mimeType,
      });

      setImageCache(prev => ({
        ...prev,
        [draft.source_file_id]: {
          pageImage: result.page_image,
          pageMime: result.page_image_mime || 'image/png',
          pageWidth: result.page_dimensions.width,
          pageHeight: result.page_dimensions.height,
        },
      }));
    } catch (err) {
      console.error('[BatchReview] Failed to load image for', draft.source_file, err);
    } finally {
      setImageLoading(null);
    }
  }, [imageCache]);

  // Load image when active tab changes
  useEffect(() => {
    if (drafts[activeTab]) {
      loadPageImage(drafts[activeTab]);
    }
  }, [activeTab, drafts, loadPageImage]);

  // Get the display value for a field (edited value takes priority over draft value)
  const getFieldValue = (draft: BatchDraft, key: string): string => {
    const edited = editedFields[draft.row_index];
    if (edited && key in edited) return edited[key];
    return (draft as Record<string, string>)[key] || '';
  };

  // Update a field value locally
  const handleFieldChange = (draft: BatchDraft, key: string, value: string) => {
    setEditedFields(prev => ({
      ...prev,
      [draft.row_index]: { ...(prev[draft.row_index] || {}), [key]: value },
    }));
  };

  // Save edited fields to sheet for a specific draft
  const saveDraftEdits = useCallback(async (rowIndex: number) => {
    const fields = editedFields[rowIndex];
    if (!fields || Object.keys(fields).length === 0) return;

    setSavingDraft(true);
    try {
      await updateBatchDraft(rowIndex, fields);
      // Update local draft data to reflect saved values
      setDrafts(prev => prev.map(d => {
        if (d.row_index !== rowIndex) return d;
        const updated = { ...d };
        for (const [k, v] of Object.entries(fields)) {
          (updated as Record<string, string>)[k] = v;
        }
        return updated;
      }));
      // Clear edited state for this row
      setEditedFields(prev => {
        const next = { ...prev };
        delete next[rowIndex];
        return next;
      });
    } catch (err) {
      console.error('[BatchReview] Failed to save edits for row', rowIndex, err);
    } finally {
      setSavingDraft(false);
    }
  }, [editedFields]);

  // Auto-save when switching tabs
  useEffect(() => {
    if (prevTabRef.current !== activeTab && drafts[prevTabRef.current]) {
      const prevDraft = drafts[prevTabRef.current];
      saveDraftEdits(prevDraft.row_index);
    }
    prevTabRef.current = activeTab;
  }, [activeTab, drafts, saveDraftEdits]);

  const handleConfirm = async (draft: BatchDraft) => {
    // Save any pending edits first
    await saveDraftEdits(draft.row_index);

    setSaving(true);
    try {
      let metadata: Record<string, unknown> = {};
      try { metadata = JSON.parse(draft.bbox_json || '{}'); } catch { /* empty */ }

      const purchaseItemsStr = getFieldValue(draft, 'purchase_items');
      const items = purchaseItemsStr
        ? purchaseItemsStr.split(';').map(s => {
            const trimmed = s.trim();
            return { name: trimmed, spec: '', qty: '1', price: '0' };
          }).filter(i => i.name)
        : [];

      const confirmation: BatchConfirmItem = {
        row_index: draft.row_index,
        edited_metadata: {
          invoice_number: getFieldValue(draft, 'invoice_id'),
          invoice_date: getFieldValue(draft, 'invoice_date'),
          seller_name: getFieldValue(draft, 'seller_name'),
          seller_id: getFieldValue(draft, 'seller_id'),
          buyer_name: getFieldValue(draft, 'buyer_name'),
          buyer_id: getFieldValue(draft, 'buyer_id'),
          sale_amount: getFieldValue(draft, 'sale_amount'),
          tax_amount: getFieldValue(draft, 'tax_amount'),
          total_amount: getFieldValue(draft, 'total_amount'),
          order_id: getFieldValue(draft, 'order_id'),
          ...metadata,
        },
        purchase_items: items,
        direction: getFieldValue(draft, 'direction') || 'purchase',
      };

      await confirmBatchDrafts([confirmation]);
      setConfirmedRows(prev => new Set(prev).add(draft.row_index));

      const nextIdx = drafts.findIndex((d, i) =>
        i > activeTab && !confirmedRows.has(d.row_index) && !skippedRows.has(d.row_index)
      );
      if (nextIdx >= 0) setActiveTab(nextIdx);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to confirm');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    const draft = drafts[activeTab];
    if (draft) {
      setSkippedRows(prev => new Set(prev).add(draft.row_index));
      const nextIdx = drafts.findIndex((d, i) =>
        i > activeTab && !confirmedRows.has(d.row_index) && !skippedRows.has(d.row_index)
      );
      if (nextIdx >= 0) setActiveTab(nextIdx);
    }
  };

  const handleConfirmAll = async () => {
    setSaving(true);
    try {
      const unprocessed = drafts.filter(
        d => !confirmedRows.has(d.row_index) && !skippedRows.has(d.row_index)
      );
      const confirmations: BatchConfirmItem[] = unprocessed.map(draft => ({
        row_index: draft.row_index,
        edited_metadata: {
          invoice_number: draft.invoice_id,
          invoice_date: draft.invoice_date,
          seller_name: draft.seller_name,
          seller_id: draft.seller_id,
          buyer_name: draft.buyer_name,
          buyer_id: draft.buyer_id,
          sale_amount: draft.sale_amount,
          tax_amount: draft.tax_amount,
          total_amount: draft.total_amount,
          order_id: draft.order_id,
        },
        purchase_items: [],
        direction: draft.direction || 'purchase',
      }));

      if (confirmations.length > 0) {
        await confirmBatchDrafts(confirmations);
        const newConfirmed = new Set(confirmedRows);
        confirmations.forEach(c => newConfirmed.add(c.row_index));
        setConfirmedRows(newConfirmed);
      }
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to confirm all');
    } finally {
      setSaving(false);
    }
  };

  const confirmedCount = confirmedRows.size;
  const skippedCount = skippedRows.size;
  const remainingCount = drafts.length - confirmedCount - skippedCount;

  if (loading) {
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl px-8 py-10 flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          <p className="text-sm text-gray-600 dark:text-gray-300">Loading drafts...</p>
        </div>
      </div>
    );
  }

  if (drafts.length === 0) {
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl px-8 py-10 flex flex-col items-center gap-4 max-w-sm">
          <p className="text-sm text-gray-600 dark:text-gray-300">No draft invoices to review.</p>
          <button onClick={onCancel} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Close
          </button>
        </div>
      </div>
    );
  }

  const currentDraft = drafts[activeTab];
  const cachedImage = currentDraft ? imageCache[currentDraft.source_file_id] : null;
  const isImageLoading = currentDraft ? imageLoading === currentDraft.source_file_id : false;

  // Parse bbox metadata for overlay
  let bboxMetadata: Record<string, { value: unknown; bbox?: BBox }> = {};
  try {
    bboxMetadata = JSON.parse(currentDraft?.bbox_json || '{}');
  } catch { /* empty */ }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-6xl mx-4 w-full h-[90vh] flex flex-col">
        {/* Header with tabs */}
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Review Batch Invoices</h2>
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span className="text-green-600">Confirmed: {confirmedCount}</span>
              <span className="text-gray-400">Skipped: {skippedCount}</span>
              <span>Remaining: {remainingCount}</span>
              <span className="text-gray-300">|</span>
              <span>Total in sheet: {stats.total}</span>
              <button onClick={onCancel}><X className="w-4 h-4" /></button>
            </div>
          </div>
          {/* Tab bar */}
          <div className="flex gap-1 overflow-x-auto pb-1">
            {drafts.map((draft, i) => {
              const isConfirmed = confirmedRows.has(draft.row_index);
              const isSkipped = skippedRows.has(draft.row_index);
              const isActive = i === activeTab;
              return (
                <button
                  key={draft.row_index}
                  onClick={() => setActiveTab(i)}
                  className={`px-3 py-1.5 text-xs rounded-t-lg border-b-2 whitespace-nowrap transition-colors ${
                    isActive
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : isConfirmed
                      ? 'border-green-400 bg-green-50 dark:bg-green-900/10 text-green-700'
                      : isSkipped
                      ? 'border-gray-300 bg-gray-50 dark:bg-gray-700 text-gray-400'
                      : draft.error
                      ? 'border-red-300 bg-red-50 dark:bg-red-900/10 text-red-600'
                      : 'border-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {isConfirmed && <Check className="w-3 h-3 inline mr-1" />}
                  {draft.error && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                  {draft.invoice_id || draft.source_file || `#${i + 1}`}
                </button>
              );
            })}
          </div>
        </div>

        {/* Split view content */}
        <div className="flex-1 flex overflow-hidden">
          {/* LEFT: Extracted fields */}
          <div className="w-[360px] shrink-0 border-r border-gray-200 dark:border-gray-700 overflow-y-auto p-4 space-y-3">
            {currentDraft && (
              <>
                {/* Source info */}
                <div className="text-xs text-gray-500">
                  {currentDraft.source_file} | {currentDraft.document_type} | {(parseFloat(currentDraft.confidence) * 100).toFixed(0)}%
                </div>

                {currentDraft.error && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-2">
                    <p className="text-xs text-red-700 dark:text-red-400">{currentDraft.error}</p>
                  </div>
                )}

                {confirmedRows.has(currentDraft.row_index) && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-2 flex items-center gap-2">
                    <Check className="w-3 h-3 text-green-600" />
                    <span className="text-xs text-green-700 dark:text-green-400">Confirmed</span>
                  </div>
                )}

                {/* Editable Fields */}
                {FIELD_ORDER.map(({ key, label, metaKey }) => {
                  const value = getFieldValue(currentDraft, key);
                  const color = BBOX_COLORS[metaKey] || '#6b7280';
                  const isEdited = editedFields[currentDraft.row_index]?.[key] !== undefined;
                  return (
                    <div key={key} className="rounded-lg border border-gray-200 dark:border-gray-600 p-2">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: color }} />
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
                        {isEdited && <span className="text-[9px] text-amber-500">edited</span>}
                      </div>
                      <input
                        value={value}
                        onChange={e => handleFieldChange(currentDraft, key, e.target.value)}
                        onBlur={() => saveDraftEdits(currentDraft.row_index)}
                        className="w-full px-2 py-1 text-sm border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-200 dark:border-gray-600"
                        placeholder={label}
                      />
                    </div>
                  );
                })}

                {/* Direction */}
                <div className="rounded-lg border border-gray-200 dark:border-gray-600 p-2">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block">Direction</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleFieldChange(currentDraft, 'direction', 'purchase')}
                      className={`flex-1 px-2 py-1 text-xs rounded border ${
                        getFieldValue(currentDraft, 'direction') === 'purchase'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700'
                          : 'border-gray-200 dark:border-gray-600 text-gray-500'
                      }`}
                    >Purchase</button>
                    <button
                      onClick={() => {
                        handleFieldChange(currentDraft, 'direction', 'sales');
                        saveDraftEdits(currentDraft.row_index);
                      }}
                      className={`flex-1 px-2 py-1 text-xs rounded border ${
                        getFieldValue(currentDraft, 'direction') === 'sales'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700'
                          : 'border-gray-200 dark:border-gray-600 text-gray-500'
                      }`}
                    >Sales</button>
                  </div>
                </div>

                {/* Purchase Items (editable) */}
                <div className="rounded-lg border border-gray-200 dark:border-gray-600 p-2">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block">Purchase Items</span>
                  <textarea
                    value={getFieldValue(currentDraft, 'purchase_items')}
                    onChange={e => handleFieldChange(currentDraft, 'purchase_items', e.target.value)}
                    onBlur={() => saveDraftEdits(currentDraft.row_index)}
                    rows={3}
                    className="w-full px-2 py-1 text-xs border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-200 dark:border-gray-600 resize-y"
                    placeholder="Item 1; Item 2; ..."
                  />
                </div>

                {/* Save indicator */}
                {savingDraft && (
                  <div className="flex items-center gap-1 text-xs text-blue-500">
                    <Loader2 className="w-3 h-3 animate-spin" /> Saving...
                  </div>
                )}
              </>
            )}
          </div>

          {/* RIGHT: Document preview with bbox overlays */}
          <div className="flex-1 overflow-auto bg-gray-100 dark:bg-gray-900 p-2">
            {isImageLoading && (
              <div className="flex items-center justify-center h-full">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                  <p className="text-xs text-gray-500">Loading document preview...</p>
                </div>
              </div>
            )}
            {!isImageLoading && cachedImage && (
              <div className="relative inline-block" style={{ lineHeight: 0 }}>
                <img
                  src={`data:${cachedImage.pageMime};base64,${cachedImage.pageImage}`}
                  alt="Invoice preview"
                  draggable={false}
                  onDragStart={e => e.preventDefault()}
                  className="block max-w-full h-auto rounded shadow-lg pointer-events-none"
                  style={{ maxHeight: 'calc(90vh - 140px)' }}
                />
                {/* BBox overlays from detection metadata */}
                {Object.entries(bboxMetadata).map(([key, field]) => {
                  if (key === 'page_dimensions' || !field?.bbox) return null;
                  const bbox = field.bbox;
                  const color = BBOX_COLORS[key] || '#6b7280';
                  return (
                    <div
                      key={key}
                      className="absolute"
                      style={{
                        left: `${(bbox.x / cachedImage.pageWidth) * 100}%`,
                        top: `${(bbox.y / cachedImage.pageHeight) * 100}%`,
                        width: `${(bbox.w / cachedImage.pageWidth) * 100}%`,
                        height: `${(bbox.h / cachedImage.pageHeight) * 100}%`,
                        border: `2px solid ${color}`,
                        backgroundColor: `${color}18`,
                        zIndex: 10,
                      }}
                    >
                      <span
                        className="absolute -top-3.5 left-0 text-[9px] font-medium px-1 rounded-t whitespace-nowrap"
                        style={{ backgroundColor: color, color: 'white' }}
                      >
                        {key.replace(/_/g, ' ')}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            {!isImageLoading && !cachedImage && currentDraft && (
              <div className="flex items-center justify-center h-full">
                <p className="text-xs text-gray-400">No preview available</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-between shrink-0">
          <div className="flex gap-2">
            <button
              onClick={handleSkip}
              disabled={!currentDraft || confirmedRows.has(currentDraft.row_index)}
              className="flex items-center gap-1 px-3 py-2 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
            >
              <SkipForward className="w-3 h-3" /> Skip
            </button>
            <button
              onClick={() => setActiveTab(Math.max(0, activeTab - 1))}
              disabled={activeTab === 0}
              className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
            >
              ← Prev
            </button>
            <button
              onClick={() => setActiveTab(Math.min(drafts.length - 1, activeTab + 1))}
              disabled={activeTab >= drafts.length - 1}
              className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
            >
              Next →
            </button>
          </div>
          <div className="flex gap-2">
            <button onClick={onCancel} className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
              Cancel
            </button>
            {currentDraft && !confirmedRows.has(currentDraft.row_index) && (
              <button
                onClick={() => handleConfirm(currentDraft)}
                disabled={saving}
                className="px-3 py-2 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Confirm'}
              </button>
            )}
            {remainingCount > 0 && (
              <button
                onClick={handleConfirmAll}
                disabled={saving}
                className="px-3 py-2 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : `Confirm All (${remainingCount})`}
              </button>
            )}
          </div>
        </div>

        {/* Error toast */}
        {error && (
          <div className="absolute bottom-16 left-4 right-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 z-20">
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            <button onClick={() => setError(null)} className="text-xs underline text-red-600 mt-1">Dismiss</button>
          </div>
        )}
      </div>
    </div>
  );
};

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export default BatchInvoiceReview;
